"use client";
import { IMAGES, WEB_COLORS } from "@/lib/constants";

import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  Link,
  useMediaQuery,
  colors,
  CircularProgress,
} from "@mui/material";
import Image from "next/image";
import React, { useEffect, useMemo, useState } from "react";
import ButtonComponent from "../Button";
import ModalComponent from "../Modal";
import DividerComponent from "../Dividers";
import PaperComponent from "../Paper";
import animationData from "@/lib/constants/animations/lottie/chatbotupload.json";
import LottieAnimation from "../animations/LottieAnimation";

import { useDeviceInfo } from "@/lib/hooks/constant.hooks";
import {
  BrowserKeyMap,
  getBrowserTabsList,
  BROWSER_GUIDE,
  ICONS,
  REQUIRED_PERMISSIONS,
} from "@/lib/utils/permission.utils";

const GuideStepDisplay = ({ browser, isWeb, isIos }) => {
  if (!browser) return null;

  // Normalize browser key for BROWSER_GUIDE
  let key = BrowserKeyMap[browser];
  const data = BROWSER_GUIDE.browsers[key];
  const steps = isWeb
    ? data?.steps?.web
    : isIos
    ? data?.steps?.mobile?.ios
    : data?.steps?.mobile?.android;

  if (!data) return null;
  return (
    <Box
      color={"#3F3F3FCC"}
      sx={{
        height: { xs: "150px", sm: "content-fit" },
      }}>
      <Typography
        fontSize={{ xs: "16px", sm: "18px", md: "24px" }}
        color={WEB_COLORS.primary}
        fontWeight={700}>
        Enable Permissions
      </Typography>
      <Typography fontSize={{ xs: "10px", sm: "12px", md: "14px" }}>
        We need a few permissions to verify your eligibility and enhance your
        loan experience. Your data is safe.
      </Typography>
      <List
        component="ol"
        sx={{
          listStyleType: "decimal",

          fontSize: { xs: "12px", sm: "14px", md: "16px" },
          pl: 4,
        }}>
        {steps?.map((stepParts, idx) => (
          <ListItem
            key={idx}
            sx={{
              display: "list-item",
              paddingY: { xs: "2px", md: "8px" },
              pl: 0,
            }}>
            <ListItemText
              primaryTypographyProps={{
                fontSize: { xs: "12px", sm: "14px", md: "16px" },
                paddingY: 0,
                // lineHeight: "0.5",
              }}
              primary={
                <Box variant="p" sx={{ lineHeight: 1.2 }}>
                  {stepParts.map((part, index) => {
                    if (part.type === "text")
                      return (
                        <span key={index} style={{ whiteSpace: "normal" }}>
                          {part.content.trim()}{" "}
                        </span>
                      );
                    if (part.type === "icon") {
                      const Icon = ICONS[part.name];
                      return Icon ? (
                        <Box
                          key={index}
                          component="span"
                          sx={{
                            mx: "4px",
                            width: { xs: "12px", sm: "14px", md: "16px" },
                            display: "inline-block",
                          }}>
                          {Icon?.src ? (
                            <Image
                              src={Icon}
                              alt={part?.name || "icon"}
                              style={{
                                verticalAlign: "text-bottom",
                                width: "100%",
                                height: "100%",
                              }}
                            />
                          ) : (
                            <Icon
                              style={{
                                verticalAlign: "text-bottom",
                                width: "100%",
                                height: "100%",
                                color: WEB_COLORS.primary,
                              }}
                            />
                          )}
                        </Box>
                      ) : null;
                    }
                    return null;
                  })}
                </Box>
              }
            />
          </ListItem>
        ))}

        {data?.note && (
          <ListItem>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: { xs: "12px", sm: "14px", md: "16px" },
              }}>
              {data?.note}
            </Typography>
          </ListItem>
        )}
      </List>
    </Box>
  );
};

const PermissionTile = (p) => {
  const isMobile = useMediaQuery("(max-width:750px)");
  return (
    <Stack position={"relative"}>
      <PaperComponent
        elevation={0}
        paperStyle={{
          backgroundColor: "rgba(229, 241, 241, 1)",
          borderRadius: 3,
          my: { xs: 0.5, md: 1 },
          p: { xs: 1, md: 2 },
        }}>
        <Stack direction="row" alignItems={"center"} gap={1}>
          <Image
            src={p.icon}
            alt="consent Image"
            height={isMobile ? 26 : 36}
            width={isMobile ? 26 : 36}
          />
          <Stack>
            <Typography fontSize={{ xs: "14px", sm: "16px", md: "18px" }}>
              {p.label}
            </Typography>
            <Typography
              fontSize={{ xs: "12px", sm: "14px", md: "16px" }}
              color="rgba(63, 63, 63, 0.8)">
              {p.subLabel}
            </Typography>
          </Stack>
        </Stack>
      </PaperComponent>
      <Box
        sx={{
          position: "absolute",
          top: { xs: 6, sm: 10, md: 25 },
          right: { xs: -22, sm: -10, md: -10 },
          display: p.optional ? "none" : "inline-block",
          transform: {
            xs: "scale(0.6)",
            sm: "scale(0.8)",
            md: "scale(0.9)",
          },
        }}>
        <Image src={IMAGES.mandatory} alt="Mandatory Icon" />
      </Box>
    </Stack>
  );
};

const PermissionsHandler = ({ children }) => {


  if (!(process.env.NEXT_PUBLIC_ENVIRONMENT === "production"))
    return <>{children}</>;


  const [missingPermissions, setMissingPermissions] = useState(
    REQUIRED_PERMISSIONS.map((p) => p.name)
  );
  const [checked, setChecked] = useState(false);
  const [notificationStatus, setNotificationStatus] = useState("unknown");
  const [showInstructions, setShowInstructions] = useState(false);
  const [browser, setBrowser] = useState("");
  const isMobile = useMediaQuery("(max-width:600px)");

  const { isIos } = useDeviceInfo();

  const allowedBrowserList = useMemo(
    () => getBrowserTabsList({ isIos, isMobile }),
    [isMobile, isIos]
  );

  const checkAndRequestPermissions = async () => {
    const stillMissing = [];

    for (const perm of REQUIRED_PERMISSIONS) {
      try {
        //  GEOLOCATION — works on all browsers
        if (perm.name === "geolocation") {
          let granted = true;
          await new Promise((resolve) => {
            if (!("geolocation" in navigator)) {
              granted = false;
              resolve();
              return;
            }

            navigator?.geolocation?.getCurrentPosition(
              () => resolve(),
              (error) => {
                console.warn("Geolocation error:", error);
                granted = false;
                resolve();
              }
              // { timeout: 10000 }
            );
          });
          if (!granted) stillMissing.push(perm.name);
        }

        //  NOTIFICATIONS — special handling for Safari
        else if (perm?.name === "notifications") {
          if (!("Notification" in window)) {
            setNotificationStatus("unsupported");
            stillMissing.push(perm.name);
            continue;
          }

          let notifPerm = Notification?.permission;
          if (notifPerm === "default") {
            notifPerm = await Notification?.requestPermission();
          }

          setNotificationStatus(notifPerm);
          if (notifPerm !== "granted") stillMissing.push(perm.name);
        }

        //  Optional fallback for other permission types (if added later)
        else if (navigator?.permissions && navigator?.permissions.query) {
          const status = await navigator?.permissions?.query({
            name: perm.name,
          });
          if (status.state !== "granted") stillMissing?.push(perm.name);
        }
      } catch (e) {
        console.error("Permission check failed:", perm.name, e);
        stillMissing.push(perm.name);
      }
    }

    setMissingPermissions(stillMissing);
    setChecked(true);
  };

  useEffect(() => {
    let permObjs = [];
    let cleanup = () => {};

    const setupListeners = async () => {
      if (!("permissions" in navigator) || !navigator?.permissions?.query)
        return;

      try {
        permObjs = await Promise.all(
          REQUIRED_PERMISSIONS.map(async (p) => {
            try {
              return await navigator?.permissions?.query({ name: p.name });
            } catch {
              return null; // Safari or unsupported permission type
            }
          })
        );

        const onChange = () => checkAndRequestPermissions();

        permObjs.forEach((obj) => {
          if (obj && obj.addEventListener) {
            obj.addEventListener("change", onChange);
          }
        });

        cleanup = () => {
          permObjs.forEach((obj) => {
            if (obj && obj.removeEventListener) {
              obj.removeEventListener("change", onChange);
            }
          });
        };
      } catch (e) {
        console.warn("Permission listener setup skipped:", e);
      }
    };

    checkAndRequestPermissions();
    setupListeners();

    return () => cleanup();
  }, []);

  // const checkAndRequestPermissions = async () => {
  //   const stillMissing = [];
  //   for (const perm of REQUIRED_PERMISSIONS) {
  //     if (perm.name === "notifications" && !perm.mandatory) {
  //       continue;
  //     }
  //     try {
  //       if (perm.name === "geolocation") {
  //         // Always use getCurrentPosition for geolocation in all browsers
  //         let granted = true;
  //         await new Promise((resolve) => {
  //           navigator.geolocation.getCurrentPosition(
  //             () => resolve(),
  //             (error) => {
  //               granted = false;
  //               console.error("Geolocation error", error);
  //               resolve();
  //             }
  //             // { timeout: 10000 }
  //           );
  //         });
  //         console.log("geolocation granted", granted);
  //         if (!granted) stillMissing.push(perm.name);
  //       }
  //       // else if (perm.name === "camera") {
  //       //   // Request camera permission on load, but stop stream immediately
  //       //   let granted = true;
  //       //   try {
  //       //     const stream = await navigator.mediaDevices.getUserMedia({
  //       //       video: true,
  //       //     });
  //       //     // Stop all tracks immediately to release camera
  //       //     stream.getTracks().forEach((track) => track.stop());
  //       //   } catch {
  //       //     granted = false;
  //       //   }
  //       //   if (!granted) stillMissing.push(perm.name);
  //       // }
  //       else {
  //         const status = await navigator.permissions.query({ name: perm.name });
  //         if (status.state !== "granted") {
  //           stillMissing.push(perm.name);
  //         }
  //       }
  //     } catch (e) {
  //       stillMissing.push(perm.name);
  //     }
  //   }
  //   try {
  //     if ("Notification" in window) {
  //       let notifPerm = Notification.permission;
  //       if (notifPerm === "default") {
  //         notifPerm = await Notification.requestPermission();
  //       }
  //       setNotificationStatus(notifPerm);
  //     } else {
  //       setNotificationStatus("unsupported");
  //     }
  //   } catch (e) {
  //     setNotificationStatus("error");
  //   }
  //   setMissingPermissions(stillMissing);
  //   setChecked(true);
  // };

  // useEffect(() => {
  //   let permObjs = [];
  //   let cleanup = () => {};

  //   const setupListeners = async () => {
  //     permObjs = await Promise.all(
  //       REQUIRED_PERMISSIONS.map((p) =>
  //         navigator?.permissions?.query({ name: p.name })
  //       )
  //     );
  //     const onChange = () => {
  //       checkAndRequestPermissions();
  //     };
  //     permObjs.forEach((obj) => obj.addEventListener("change", onChange));
  //     cleanup = () => {
  //       permObjs.forEach((obj) => obj.removeEventListener("change", onChange));
  //     };
  //   };

  //   checkAndRequestPermissions();
  //   setupListeners();

  //   return () => {
  //     cleanup();
  //   };
  // }, []);

  if (!checked) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          textAlign: "center",
          p: 3,
          color: "text.primary",
        }}
      >
        <CircularProgress size={30} disableShrink sx={{ mb: 3 }} />
        <Typography variant="h6" fontWeight={500}>
          Verifying your permissions...
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Hang tight, this won’t take long.
        </Typography>
      </Box>
    );
  }

  const active = "#E5F1F1";
  const inActive = "#F8FAFA";

  let permissionsGif = undefined;
  if (browser) {
    permissionsGif =
      BROWSER_GUIDE.browsers[BrowserKeyMap[browser]].gifs?.[
        isMobile ? (isIos ? "ios" : "mobile") : "web"
      ];
  }

  if (browser && !permissionsGif && isMobile) {
    // fallback for mobile
    permissionsGif =
      BROWSER_GUIDE.browsers[BrowserKeyMap[browser]].gifs?.mobile;
  }

  const mandatoryPermissionMissingList = REQUIRED_PERMISSIONS.filter(
    (p) => missingPermissions.includes(p.name) && p.mandatory
  );

  // const

  return (
    <>
      {mandatoryPermissionMissingList?.length === 0 && children}

      <ModalComponent
        open={Boolean(mandatoryPermissionMissingList.length)}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            border: "1px solid #F0F0F099",
            backgroundColor: "#F8FAFA",
            width: { xs: "95%", md: "80%", lg: "70%" },
            boxShadow: "none",
            m: { xs: 0, md: 2 },
          },
          elevation: 0,
        }}
        maxWidth={"lg"}
        hideBackdrop
        closeButtonRequired={false}>
        <Stack>
          <Typography
            variant="h6"
            fontSize={{ xs: "16px", sm: "26px", md: "32px" }}
            color={WEB_COLORS.primary}>
            Allow Permission
          </Typography>
          <Typography
            color={"#727272"}
            fontSize={{ xs: "12px", sm: "14px", md: "16px" }}>
            To ensure a smooth and secure loan experience, we request access to
            a few essential permissions. Your data is safe and used only to
            verify your eligibility and enhance your journey.
          </Typography>
        </Stack>

        <Box
          sx={{
            my: {
              xs: 1,
              md: 0,
            },
          }}>
          {REQUIRED_PERMISSIONS.filter(
            (p) => missingPermissions.includes(p.name) && p.mandatory
          ).map((p) => (
            <PermissionTile key={p.name} {...p} />
          ))}
          {notificationStatus !== "granted" && (
            <PermissionTile
              icon={IMAGES.notification}
              label={"Notifications"}
              optional={true}
              subLabel={
                REQUIRED_PERMISSIONS.find((p) => p.name === "notifications")
                  .subLabel
              }
            />
          )}
        </Box>

        <Typography
          color={"#727272"}
          fontSize={{ xs: "12px", sm: "14px", md: "16px" }}>
          The above data points collected, shared, and transmitted by our
          third-party are used for building comprehensive credit risk assessment
          and credit profile. This information shall be securely shared with our
          providers, affiliates and partners
        </Typography>
        <Stack
          sx={{
            position: { xs: "static", sm: "static" },
            bottom: { xs: 0, sm: "auto" },
            zIndex: 100,
            mb: 2,
            mt: 4,
            width: { xs: "100%", sm: "auto" },
            left: 0,
          }}>
          <ButtonComponent
            fullWidth
            onClick={() => {
              setShowInstructions(true);
            }}
            sx={{
              fontWeight: 400,
              fontSize: { xs: 12, sm: 14, md: 16 },
              textTransform: "capitalize",
              py: 1.5,
              borderRadius: 2,
            }}>
            View instructions for enabling permissions
          </ButtonComponent>
        </Stack>

        {/* Browser specific directions modal */}
        <ModalComponent
          open={showInstructions}
          maxWidth="md"
          closeButtonRequired={false}
          contentSx={{
            px: { xs: 1, sm: 2, md: 4 },
            pt: { xs: 0 },
            pb: { xs: 1, md: 4 },
          }}
          handleModal={() => {
            setBrowser("");
            setShowInstructions(false);
          }}>
          <Stack mt={2} gap={1} alignItems={"center"}>
            <Stack gap={0.5} textAlign={"center"}>
              <Typography
                fontSize={{ xs: "16px", sm: "26px", md: "32px" }}
                fontWeight={700}>
                Allow Permissions to Proceed
              </Typography>
              <Typography
                fontSize={{ xs: "12px", sm: "14px", md: "16px" }}
                color={WEB_COLORS.primary}>
                Choose your preferred browser
              </Typography>
            </Stack>
            <Stack direction="row" mt={2} gap={1} flexWrap={"wrap"}>
              {allowedBrowserList
                .filter((i) => !i.hidden)
                .map(({ key, label, icon, endIcon, sx = {} }) => (
                  <Button
                    key={key}
                    disableElevation
                    disableFocusRipple
                    disableTouchRipple
                    disableRipple
                    variant={browser === key ? "contained" : "outlined"}
                    onClick={() => setBrowser(key)}
                    sx={{
                      borderRadius: 2,
                      border:
                        browser === key
                          ? `1px solid ${WEB_COLORS.primary}`
                          : `1px solid ${inActive}`,
                      backgroundColor: browser === key ? active : inActive,
                      color: browser === key ? WEB_COLORS.primary : "#727272",
                      textTransform: "capitalize",
                      minWidth: 120,
                      textWrap: "nowrap",
                      flex: 1,
                      transition: "background 0.2s, color 0.2s",
                      boxShadow: browser === key ? 2 : 0,
                      "&:hover": {
                        backgroundColor:
                          browser === key ? `${active} !important` : inActive,
                      },
                      "@media (hover: none)": {
                        backgroundColor:
                          browser === key ? `${active} !important` : inActive,
                        color: browser === key ? WEB_COLORS.primary : "#727272",
                      },
                      ...sx,
                    }}
                    startIcon={
                      icon ? (
                        <Image
                          src={icon}
                          alt={label}
                          style={{ height: 24, width: 24 }}
                        />
                      ) : null
                    }
                    endIcon={
                      endIcon ? (
                        <Image
                          src={endIcon}
                          alt={label}
                          style={{
                            height: 24,
                            width: 24,
                            transform: "scale(1.45)",
                          }}
                        />
                      ) : null
                    }>
                    {label}
                  </Button>
                ))}
            </Stack>
          </Stack>
          {browser && (
            <Stack gap={2} my={2}>
              <DividerComponent orientation="horizontal" />
              <PaperComponent
                elevation={0}
                paperStyle={{
                  backgroundColor: "rgba(229, 241, 241, 1)",
                  borderRadius: 3,
                  p: 3,
                }}>
                <Stack
                  gap={4}
                  // direction={"row"}
                  sx={{
                    height: { xs: "content-fit", sm: "300px" },
                    gap: { xs: "10px", sm: "14px", md: "20px" },
                    flexDirection: { xs: "column", sm: "row" },
                  }}>
                  <Box
                    sx={{ width: { xs: "100%", sm: "40%" }, height: "100%" }}>
                    {/* <LottieAnimation
                      animationData={animationData}
                      sx={{
                        borderRadius: "15px",
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    /> */}
                    <Image
                      unoptimized
                      src={permissionsGif}
                      alt="permission guider"
                      style={{
                        borderRadius: "15px",
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      overflow: "auto",
                    }}>
                    {GuideStepDisplay({ browser, isWeb: !isMobile, isIos })}
                  </Box>
                </Stack>
              </PaperComponent>
            </Stack>
          )}
          <Stack
            my={2}
            sx={{
              position: { xs: "sticky", sm: "static" },
              bottom: { xs: 0, sm: "auto" },
              zIndex: 1200,
              // background: { xs: "rgba(229, 241, 241, 0.95)", sm: "none" },
              // p: { xs: 2, sm: 0 },
              width: { xs: "100%", sm: "auto" },
              left: 0,
              display: browser ? "block" : "none",
            }}>
            <ButtonComponent
              fullWidth
              onClick={() => {
                setShowInstructions(false);
                setBrowser("");
              }}
              sx={{
                fontSize: 14,
                textTransform: "capitalize",
                py: 1.5,
                borderRadius: 2,
              }}>
              Okay
            </ButtonComponent>
          </Stack>
        </ModalComponent>
      </ModalComponent>
    </>
  );
};

export default PermissionsHandler;
